import { z } from "zod";
import { AssetVisibilitySchema } from "./asset-visibility.js";

export { type AssetVisibility, AssetVisibilitySchema } from "./asset-visibility.js";

// ---------------------------------------------------------------------------
// Shared shapes
// ---------------------------------------------------------------------------

export const DashboardHealthSchema = z.enum(["ok", "partial", "failing", "never_run"]);
export type DashboardHealth = z.infer<typeof DashboardHealthSchema>;

export const DashboardComponentKindSchema = z.enum([
  "metric-card",
  "info-card",
  "line-chart",
  "bar-chart",
  "table",
]);
export type DashboardComponentKind = z.infer<typeof DashboardComponentKindSchema>;

export const DashboardComponentSizeSchema = z.enum(["small", "medium", "large"]);
export type DashboardComponentSize = z.infer<typeof DashboardComponentSizeSchema>;

export const DeltaPolaritySchema = z.enum(["higher-is-better", "lower-is-better", "neutral"]);
export type DeltaPolarity = z.infer<typeof DeltaPolaritySchema>;

const DashboardSummarySchema = z.object({
  dashboardId: z.string().min(1),
  agentId: z.string().min(1),
  name: z.string(),
  description: z.string().optional(),
  visibility: AssetVisibilitySchema,
  health: DashboardHealthSchema,
  createdAt: z.number(),
  updatedAt: z.number(),
  lastRunAt: z.number().optional(),
});

const DashboardComponentSchema = z.object({
  id: z.string().min(1),
  kind: DashboardComponentKindSchema,
  title: z.string(),
  description: z.string().optional(),
  size: DashboardComponentSizeSchema,
  lastRunStatus: z.enum(["ok", "error", "never_run"]).optional(),
  lastRunAt: z.number().optional(),
  /** Stored as `errorKind: message`. */
  lastError: z.string().optional(),
});

const DashboardWithComponentsSchema = z.object({
  dashboardId: z.string().min(1),
  agentId: z.string().min(1),
  name: z.string(),
  description: z.string().optional(),
  createdByUserId: z.string().min(1).optional(),
  visibility: AssetVisibilitySchema,
  viewerCanChangeVisibility: z.boolean(),
  health: DashboardHealthSchema,
  createdAt: z.number(),
  updatedAt: z.number(),
  components: z.array(DashboardComponentSchema),
});

const DashboardComponentResultSchema = z.object({
  componentId: z.string().min(1),
  ok: z.boolean(),
  /** Validated payload (when `ok: true`). The browser dispatches on `kind`
   *  before render, so this stays `unknown` at the HTTP boundary. */
  result: z.unknown().optional(),
  /** Truncated JSON preview, sized to the gateway's `maxResultBytes`.
   *  Present when the component has either a fresh result OR a prior
   *  last-good result the UI can render in the stale state. */
  lastResultPreview: z.string().optional(),
  errorKind: z.string().optional(),
  error: z.string().optional(),
  lastRunAt: z.number(),
  materialisedAt: z.number().optional(),
  durationMs: z.number(),
  toolCallsMade: z.number(),
  /** Diagnostic ring buffer from the run. The UI shows this only to admins. */
  logs: z.array(z.string()).optional(),
});

const DashboardRunPayloadSchema = z.object({
  status: DashboardHealthSchema,
  components: z.array(DashboardComponentResultSchema),
});

// ---------------------------------------------------------------------------
// Browser API: dashboards.list
// ---------------------------------------------------------------------------

export const DashboardListPayloadSchema = z.object({}).strict();

export const DashboardListDataSchema = z.object({
  dashboards: z.array(DashboardSummarySchema),
});

// ---------------------------------------------------------------------------
// Browser API: dashboards.getByIds
// ---------------------------------------------------------------------------

export const DashboardGetByIdsPayloadSchema = z
  .object({ dashboardIds: z.array(z.string().min(1)).min(1).max(200) })
  .strict();

export const DashboardGetByIdsDataSchema = z.object({
  dashboards: z.array(DashboardSummarySchema),
});

// ---------------------------------------------------------------------------
// Browser API: dashboards.get
// ---------------------------------------------------------------------------

export const DashboardGetPayloadSchema = z.object({ dashboardId: z.string().min(1) }).strict();

export const DashboardGetDataSchema = z.object({
  dashboard: DashboardWithComponentsSchema,
});

// ---------------------------------------------------------------------------
// Browser API: dashboards.delete
// ---------------------------------------------------------------------------

export const DashboardDeletePayloadSchema = z.object({ dashboardId: z.string().min(1) }).strict();

export const DashboardDeleteDataSchema = z.object({ ok: z.literal(true) }).strict();

// ---------------------------------------------------------------------------
// Browser API: dashboards.run
// ---------------------------------------------------------------------------

export const DashboardRunPayloadInputSchema = z
  .object({
    dashboardId: z.string().min(1),
    triggeredByUserId: z.string().optional(),
  })
  .strict();

export const DashboardRunDataSchema = z.object({ run: DashboardRunPayloadSchema });

// ---------------------------------------------------------------------------
// Browser API: dashboards.runComponent
// ---------------------------------------------------------------------------

export const DashboardRunComponentPayloadSchema = z
  .object({
    dashboardId: z.string().min(1),
    componentId: z.string().min(1),
    triggeredByUserId: z.string().optional(),
  })
  .strict();

export const DashboardRunComponentDataSchema = z.object({
  component: DashboardComponentResultSchema,
});
